import { jsPDF } from "jspdf";

interface QuotationData {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  clientName: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  quoteNumber: string;
  quoteDate: string;
  validityDate: string;
  taxRate: string;
  notes: string;
  lineItems: Array<{
    id: string;
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
  }>;
}

const LAYOUT = {
  pageWidth: 210,
  pageHeight: 297,
  marginLeft: 20,
  marginRight: 20,
  marginTop: 20,
  marginBottom: 20,
  contentWidth: 170,
};

const COLORS = {
  primary: [15, 23, 42] as [number, number, number], // slate-900
  accent: [245, 158, 11] as [number, number, number], // amber-500
  text: [26, 24, 21] as [number, number, number], // slate-900
  muted: [107, 114, 128] as [number, number, number], // slate-500
  border: [229, 229, 229] as [number, number, number], // slate-200
  lightBg: [248, 246, 243] as [number, number, number], // background
};

function setFont(doc: jsPDF, size: number, style: "normal" | "bold" = "normal") {
  doc.setFontSize(size);
  doc.setFont("helvetica", style);
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function createQuotePDFDocument(
  data: QuotationData,
  subtotal: number,
  taxAmount: number,
  total: number
): jsPDF {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  let y = LAYOUT.marginTop;

  // Header background - increased height for more space
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, LAYOUT.pageWidth, 55, "F");

  // Company Name and QUOTE title
  doc.setTextColor(255, 255, 255);
  setFont(doc, 28, "bold");
  doc.text("QUOTE", LAYOUT.marginLeft, y + 15);

  setFont(doc, 11, "bold");
  doc.text(data.companyName, LAYOUT.marginLeft, y + 26);

  // Accent line
  doc.setFillColor(...COLORS.accent);
  doc.rect(LAYOUT.marginLeft, y + 31, 50, 2, "F");

  // Quote number and date (right side, still in header)
  doc.setTextColor(255, 255, 255);
  setFont(doc, 9, "normal");
  doc.text(`Quote #: ${data.quoteNumber}`, LAYOUT.pageWidth - LAYOUT.marginRight, y + 12, {
    align: "right",
  });
  doc.text(`Date: ${formatDate(data.quoteDate)}`, LAYOUT.pageWidth - LAYOUT.marginRight, y + 19, {
    align: "right",
  });
  doc.text(
    `Valid until: ${formatDate(data.validityDate)}`,
    LAYOUT.pageWidth - LAYOUT.marginRight,
    y + 26,
    {
      align: "right",
    }
  );

  y = 62;

  // Company Details (left)
  doc.setTextColor(...COLORS.text);
  setFont(doc, 9, "bold");
  doc.text("From:", LAYOUT.marginLeft, y);

  y += 6;
  setFont(doc, 9, "normal");
  doc.text(data.companyName, LAYOUT.marginLeft, y);
  y += 5;
  doc.text(data.companyAddress, LAYOUT.marginLeft, y);
  y += 5;
  doc.text(data.companyPhone, LAYOUT.marginLeft, y);
  y += 5;
  doc.text(data.companyEmail, LAYOUT.marginLeft, y);

  // Bill To (right)
  y = 62;
  setFont(doc, 9, "bold");
  doc.text("Bill To:", LAYOUT.pageWidth - LAYOUT.marginRight - 50, y);

  y += 6;
  setFont(doc, 9, "normal");
  doc.text(data.clientName || "[Client Name]", LAYOUT.pageWidth - LAYOUT.marginRight - 50, y);
  y += 5;
  doc.text(data.clientAddress || "[Client Address]", LAYOUT.pageWidth - LAYOUT.marginRight - 50, y);
  y += 5;
  doc.text(data.clientPhone || "", LAYOUT.pageWidth - LAYOUT.marginRight - 50, y);
  y += 5;
  doc.text(data.clientEmail || "", LAYOUT.pageWidth - LAYOUT.marginRight - 50, y);

  // Items table
  y = 100;

  // Table header background
  doc.setFillColor(...COLORS.lightBg);
  doc.rect(LAYOUT.marginLeft, y - 5, LAYOUT.contentWidth, 8, "F");

  // Table header
  doc.setTextColor(...COLORS.text);
  setFont(doc, 9, "bold");
  doc.text("Description", LAYOUT.marginLeft + 2, y);
  doc.text("Qty", 105, y, { align: "center" });
  doc.text("Unit Price", 140, y, { align: "right" });
  doc.text("Total", LAYOUT.pageWidth - LAYOUT.marginRight, y, { align: "right" });

  // Table rows
  y += 10;
  setFont(doc, 9, "normal");

  data.lineItems.forEach((item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unitPrice) || 0;
    const itemTotal = quantity * unitPrice;

    const lines = doc.splitTextToSize(item.description, 60);
    const lineHeight = lines.length > 1 ? lines.length * 4 : 5;

    // Check if we need a new page
    if (y + lineHeight > LAYOUT.pageHeight - LAYOUT.marginBottom - 50) {
      doc.addPage();
      y = LAYOUT.marginTop;
    }

    doc.text(lines, LAYOUT.marginLeft + 2, y);
    doc.text(quantity.toString(), 105, y, { align: "center" });
    doc.text(formatCurrency(unitPrice), 140, y, { align: "right" });
    doc.text(formatCurrency(itemTotal), LAYOUT.pageWidth - LAYOUT.marginRight, y, {
      align: "right",
    });

    y += lineHeight + 2;
  });

  // Separator line
  y += 3;
  doc.setDrawColor(...COLORS.border);
  doc.line(LAYOUT.marginLeft, y, LAYOUT.pageWidth - LAYOUT.marginRight, y);

  // Totals section
  y += 8;
  setFont(doc, 9, "normal");
  doc.setTextColor(...COLORS.muted);

  doc.text("Subtotal:", 120, y, { align: "left" });
  doc.text(formatCurrency(subtotal), LAYOUT.pageWidth - LAYOUT.marginRight, y, { align: "right" });

  y += 6;
  const taxRate = parseFloat(data.taxRate) || 0;
  doc.text(`Tax (${taxRate}%):`, 120, y, { align: "left" });
  doc.text(formatCurrency(taxAmount), LAYOUT.pageWidth - LAYOUT.marginRight, y, { align: "right" });

  // Total box
  y += 8;
  doc.setFillColor(...COLORS.accent);
  doc.rect(120, y - 4, LAYOUT.pageWidth - LAYOUT.marginRight - 120, 10, "F");

  setFont(doc, 14, "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Total:", 122, y + 2);
  doc.text(formatCurrency(total), LAYOUT.pageWidth - LAYOUT.marginRight - 2, y + 2, {
    align: "right",
  });

  // Notes section
  if (data.notes.trim()) {
    y += 20;
    doc.setTextColor(...COLORS.text);
    setFont(doc, 9, "bold");
    doc.text("Terms & Notes:", LAYOUT.marginLeft, y);

    y += 5;
    setFont(doc, 8, "normal");
    doc.setTextColor(...COLORS.muted);
    const noteLines = doc.splitTextToSize(data.notes, LAYOUT.contentWidth);
    doc.text(noteLines, LAYOUT.marginLeft, y);
  }

  // Footer
  doc.setTextColor(...COLORS.muted);
  setFont(doc, 8, "normal");
  doc.text(
    "Thank you for your business!",
    LAYOUT.pageWidth / 2,
    LAYOUT.pageHeight - LAYOUT.marginBottom + 5,
    { align: "center" }
  );

  return doc;
}

// Server-side function to generate PDF as buffer
export function generateQuotePDFAsBuffer(
  data: QuotationData,
  subtotal: number,
  taxAmount: number,
  total: number
): Buffer {
  const doc = createQuotePDFDocument(data, subtotal, taxAmount, total);
  const pdfBytes = doc.output("arraybuffer");
  return Buffer.from(pdfBytes);
}

// Browser function - wraps the core PDF generation for client-side display
export function generateQuotePDF(
  data: QuotationData,
  subtotal: number,
  taxAmount: number,
  total: number
) {
  const doc = createQuotePDFDocument(data, subtotal, taxAmount, total);

  // Generate PDF as blob and open in new tab
  // This approach works better in sandboxed iframe environments
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);

  // Open PDF in a new tab for viewing/downloading
  // This bypasses iframe sandbox download restrictions
  const newWindow = window.open(pdfUrl, "_blank");

  // If popup was blocked, fallback to direct navigation
  if (!newWindow) {
    // Create a temporary link as fallback
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `quote-${data.quoteNumber.replace(/\//g, "-")}.pdf`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Clean up the blob URL after a delay to allow download/view
  setTimeout(() => {
    URL.revokeObjectURL(pdfUrl);
  }, 10000);
}
